import {
  Badge,
  Button,
  COLOR_PALETTE,
  Ellipsis,
  Flex,
  Skeleton,
  Text,
  desktopFirstMediaQuery,
} from '@inflearn/ds-react-next';
import { isNil } from 'lodash-es';
import Script from 'next/script';
import { useEffect, useRef, useState } from 'react';

import { NEXT_PUBLIC_CAPTCHA_SITE_KEY, NEXT_PUBLIC_ENV } from '../../appConfig';

interface UseCaptchaProps {
  enabled?: boolean;
}

export const useCaptcha = ({ enabled = true }: UseCaptchaProps = {}) => {
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<HTMLDivElement>(null);
  const callbackRef = useRef<((token: string) => void) | null>(null);

  const [isPending, setIsPending] = useState(false);

  const isCaptchaTokenExist = !isNil(captchaToken);
  const captchaClassName = 'g-recaptcha';

  useEffect(
    function initializeCaptchaWidget() {
      if (!enabled) {
        return;
      }

      window.onCaptchaloadCallback = () => {
        window.turnstile.render(captchaRef.current ?? captchaClassName, {
          sitekey: NEXT_PUBLIC_CAPTCHA_SITE_KEY,
        });

        getCaptchaToken();
      };

      // next/script는 이미 로드된 src를 다시 삽입하지 않아, 같은 세션에서 재마운트되면 onload 콜백이 불리지 않는다.
      if (window.turnstile) {
        window.onCaptchaloadCallback();
      }
    },
    [enabled],
  );

  useEffect(() => {
    notify();
  }, [isCaptchaTokenExist]);

  function runWhenCaptchaExist(callback: ((token: string) => void) | null) {
    setIsPending(true);
    callbackRef.current = callback;
    notify();
  }

  function notify() {
    if (!isCaptchaTokenExist || isNil(callbackRef.current)) {
      return;
    }

    callbackRef.current(captchaToken ?? '');
    callbackRef.current = null;
    setIsPending(false);
  }

  /**
   * 한번 사용한 Token을 다시 사용할 경우 <이미 검증된>을 반환하며 실패함
   * 따라서 API 호출이후 토큰을 새로 받아야함
   * @see https://inflab-team.slack.com/archives/C050059E0ET/p1727261715425079?thread_ts=1727247588.460789&cid=C050059E0ET
   */
  function resetCaptchaToken() {
    window.turnstile.reset();
    removeCaptchaToken();
  }

  function getCaptchaToken() {
    window.turnstile.execute(captchaRef.current ?? captchaClassName, {
      sitekey: NEXT_PUBLIC_CAPTCHA_SITE_KEY,
      callback: updateCaptchaToken,
    });
  }

  function removeCaptchaToken() {
    updateCaptchaToken();
  }

  function updateCaptchaToken(token?: string) {
    setCaptchaToken(isNil(token) ? null : token);
  }

  function DevTools() {
    if (NEXT_PUBLIC_ENV === 'production') {
      return null;
    }

    return (
      <Flex
        justify="center"
        direction="column"
        gap={4}
        opacity={0.8}
        pos="absolute"
        top={40}
        left={40}
        p="sm"
        w={200}
        styles={{
          root: {
            borderRadius: 8,
            border: `1px solid ${COLOR_PALETTE.gray[5]}`,
            [desktopFirstMediaQuery.mobile]: { display: 'none' },
          },
        }}
      >
        <Text fw={900} size="sm">
          개발용
        </Text>
        <Text>CAPTCHA Token: </Text>
        {isCaptchaTokenExist ? (
          <Badge color="orange" w="100%">
            <Ellipsis lineClamp={1}>{captchaToken}</Ellipsis>
          </Badge>
        ) : (
          <Skeleton bg="dark" w="100%" h={20} />
        )}
        <Button variant="default" onClick={resetCaptchaToken}>
          리셋
        </Button>
        <Button variant="default" onClick={removeCaptchaToken}>
          제거
        </Button>
        <Button variant="default" onClick={() => updateCaptchaToken('이상한 토큰')}>
          이상한 토큰
        </Button>
      </Flex>
    );
  }

  return {
    isCaptchaTokenExist,
    captchaToken,
    captchaRef,
    captchaClassName,
    resetCaptchaToken,
    runWhenCaptchaExist,
    DevTools,
    isPending,
  };
};

export type Captcha = ReturnType<typeof useCaptcha>;

export const CaptchaScript = () => {
  return (
    <Script
      src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onCaptchaloadCallback"
      async
      defer
    />
  );
};
